import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Layout, { useViewport, TopBar } from '../../components/Layout';
import { Meta, Display } from '../../components/Atoms';

/**
 * CareShell - wspólny layout dla huba "Pielęgnacja" (Care).
 *
 * Mobile: pełna szerokość, back-link do listy huba.
 * Desktop: sidebar 3 sekcje + main z treścią.
 *
 * Sekcje:
 *   /care            -> Opryski (default)
 *   /care/fertilization -> Nawożenie
 *   /care/add        -> Nowy zabieg
 */

const SECTIONS = [
  { key: 'sprays',        to: '/care',               label: 'Opryski' },
  { key: 'fertilization', to: '/care/fertilization', label: 'Nawożenie' },
  { key: 'add',           to: '/care/add',           label: 'Nowy zabieg' },
];

function activeKey(pathname) {
  if (pathname === '/care' || pathname === '/care/') return 'sprays';
  if (pathname.startsWith('/care/fertilization')) return 'fertilization';
  if (pathname.startsWith('/care/add')) return 'add';
  return 'sprays';
}

function CareSidebar({ active }) {
  return (
    <aside style={{
      width: 220,
      flexShrink: 0,
      borderRight: '1px solid var(--t-line)',
      padding: '32px 0',
      position: 'sticky',
      top: 0,
      alignSelf: 'flex-start',
      maxHeight: '100vh',
      overflowY: 'auto',
    }}>
      <div style={{ padding: '0 24px 24px' }}>
        <Meta>Pielęgnacja</Meta>
      </div>
      <nav style={{ padding: '0 12px' }}>
        {SECTIONS.map(s => {
          const on = s.key === active;
          return (
            <Link
              key={s.key}
              to={s.to}
              style={{
                display: 'block', padding: '12px 14px',
                borderRadius: 9, marginBottom: 2,
                background: on ? 'rgba(168, 196, 158, 0.10)' : 'transparent',
                color: on ? 'var(--t-text)' : 'var(--t-text-dim)',
                fontFamily: 'var(--t-sans)', fontSize: 14,
                fontWeight: on ? 500 : 400,
                letterSpacing: '-.01em',
                textDecoration: 'none',
                transition: 'background 200ms ease',
              }}
            >{s.label}</Link>
          );
        })}
      </nav>
    </aside>
  );
}

export default function CareShell({ children, title, subtitle, eyebrow }) {
  const location = useLocation();
  const active = activeKey(location.pathname);
  const { isPhone, isTablet } = useViewport();

  if (isPhone || isTablet) {
    return (
      <Layout>
        <div style={{ maxWidth: 480, margin: '0 auto', paddingTop: 20 }}>
          <div style={{ padding: '0 20px 16px', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {SECTIONS.map(s => {
              const on = s.key === active;
              return (
                <Link
                  key={s.key}
                  to={s.to}
                  style={{
                    fontFamily: 'var(--t-mono)', fontSize: 11,
                    letterSpacing: '.18em', textTransform: 'uppercase',
                    color: on ? 'var(--t-sage)' : 'var(--t-text-mute)',
                    textDecoration: 'none',
                    borderBottom: on ? '1px solid var(--t-sage)' : 'none',
                    paddingBottom: 4,
                  }}
                >{s.label}</Link>
              );
            })}
          </div>
          <div style={{ padding: '0 24px 24px' }}>
            {eyebrow && <Meta color="sage" style={{ marginBottom: 8 }}>{eyebrow}</Meta>}
            {title && (
              <Display size={'clamp(32px, 7vw, 42px)'} style={{
                display: 'block', letterSpacing: '-.025em', lineHeight: 0.95,
                marginBottom: subtitle ? 12 : 24,
              }}>{title}</Display>
            )}
            {subtitle && (
              <div style={{
                fontSize: 14, color: 'var(--t-text-dim)',
                lineHeight: 1.55, marginBottom: 24,
              }}>{subtitle}</div>
            )}
          </div>
          <div style={{ padding: '0 24px 60px' }}>{children}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout topBar={<TopBar
      left={<Meta>Pielęgnacja · {SECTIONS.find(s => s.key === active)?.label || ''}</Meta>}
    />}>
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 80px)' }}>
        <CareSidebar active={active} />
        <div style={{ flex: 1, padding: '40px 48px 60px', maxWidth: 920 }}>
          {eyebrow && <Meta color="sage" style={{ marginBottom: 10 }}>{eyebrow}</Meta>}
          {title && (
            <Display size={44} style={{
              display: 'block', letterSpacing: '-.03em',
              marginBottom: subtitle ? 14 : 28,
            }}>{title}</Display>
          )}
          {subtitle && (
            <div style={{
              fontSize: 15, color: 'var(--t-text-dim)',
              lineHeight: 1.55, marginBottom: 28, maxWidth: 560,
            }}>{subtitle}</div>
          )}
          {children}
        </div>
      </div>
    </Layout>
  );
}

export const careInputStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: 12,
  background: 'var(--t-bg)',
  border: '1px solid var(--t-line-2)',
  color: 'var(--t-text)',
  fontFamily: 'var(--t-sans)',
  fontSize: 16,
  letterSpacing: '-.01em',
  outline: 'none',
  boxSizing: 'border-box',
};

export function CareLabel({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} style={{
      display: 'block', marginBottom: 8,
      fontFamily: 'var(--t-mono)', fontSize: 10,
      letterSpacing: '.22em', textTransform: 'uppercase',
      color: 'var(--t-text-dim)',
    }}>{children}</label>
  );
}

export function CareSection({ title, description, children, action }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{
        paddingBottom: 12, marginBottom: 18,
        borderBottom: '1px solid var(--t-line-2)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 14,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Display size={20} style={{ display: 'block', marginBottom: description ? 6 : 0 }}>
            {title}
          </Display>
          {description && (
            <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.5 }}>
              {description}
            </div>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
